/**
 * Session Management Configuration
 * 
 * This file contains the configuration for the session management system.
 * All values are in milliseconds.
 */

interface SessionConfig {
  // Time of inactivity before session expires (default: 15 minutes)
  inactivityTimeout: number;
  
  // Warning period before session expiration (default: 5 minutes)
  warningPeriod: number;
  
  // Maximum session duration regardless of activity (default: 24 hours)
  maxSessionDuration: number;
}

// Default configuration values (test values)
const defaultConfig: SessionConfig = {
  inactivityTimeout: 20 * 1000, // 20 seconds in milliseconds
  warningPeriod: 10 * 1000,     // 10 seconds in milliseconds
  maxSessionDuration: 60 * 60 * 1000, // 1 hour in milliseconds
};

// Original values (commented for testing):
// const defaultConfig: SessionConfig = {
//   inactivityTimeout: 15 * 60 * 1000, // 15 minutes in milliseconds
//   warningPeriod: 5 * 60 * 1000,      // 5 minutes in milliseconds
//   maxSessionDuration: 24 * 60 * 60 * 1000, // 24 hours in milliseconds
// };

/**
 * Get session configuration with environment overrides if available
 */
export const getSessionConfig = (): SessionConfig => {
  // Check for environment variables (for production builds)
  const envConfig: Partial<SessionConfig> = {};
  
  if (process.env.REACT_APP_INACTIVITY_TIMEOUT) {
    envConfig.inactivityTimeout = parseInt(process.env.REACT_APP_INACTIVITY_TIMEOUT) * 1000;
  }
  
  if (process.env.REACT_APP_WARNING_PERIOD) {
    envConfig.warningPeriod = parseInt(process.env.REACT_APP_WARNING_PERIOD) * 1000;
  }
  
  if (process.env.REACT_APP_MAX_SESSION_DURATION) {
    envConfig.maxSessionDuration = parseInt(process.env.REACT_APP_MAX_SESSION_DURATION) * 1000;
  }
  
  // Return merged configuration
  return { ...defaultConfig, ...envConfig };
};

// Export the configuration interface for type checking
export type { SessionConfig };
